/**
 * The account reads and writes that decide where a player lands after login
 * or the intro, through the RTK endpoints the rest of the app caches them
 * with. Each read is forced fresh, since a player who just signed in or
 * created a character must not be routed by stale data.
 */
import type { AccountSettings } from '@sorrem/shared';
import { toCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { sorremApi, type AppDispatch } from '@sorrem-client/ui/store';

/** The account's settings, or null when they could not be read. */
export async function loadAccountSettings(dispatch: AppDispatch, accountId: string): Promise<AccountSettings | null> {
    try {
        return await dispatch(sorremApi.endpoints.getAccountSettings.initiate(accountId, { subscribe: false, forceRefetch: true })).unwrap();
    } catch {
        return null;
    }
}

/** How many characters the account has, or null when they could not be read. */
export async function loadAccountCharacterCount(dispatch: AppDispatch, accountId: string): Promise<number | null> {
    try {
        const characters = await dispatch(
            sorremApi.endpoints.getCharacters.initiate(toCanonicalAccountId(accountId), { subscribe: false, forceRefetch: true }),
        ).unwrap();
        return characters.length;
    } catch {
        return null;
    }
}

/**
 * Records that the account has seen the intro proclamation. A failed save
 * resolves anyway: the player just sees the proclamation again next login.
 */
export async function markIntroProclamationSeen(dispatch: AppDispatch, accountId: string): Promise<void> {
    await dispatch(sorremApi.endpoints.patchAccountSettings.initiate({ accountId, body: { hasSeenIntroProclamation: true } }));
}
